// src/services/branchDisplayService.js
import api from "./api";

const BASE_URL = "/admin-branch-display";

// ============================================
// STORE DISPLAY & BRANDING
// ============================================

const getBranchDisplay = async (branchCode) => {
  const response = await api.get(`${BASE_URL}/${branchCode}`);
  return response.data;
};

const getPublicBranchDisplay = async (branchCode) => {
  const response = await api.get(`${BASE_URL}/${branchCode}/public`);
  return response.data;
};

const updateBranding = async (branchCode, brandingData) => {
  const response = await api.put(
    `${BASE_URL}/${branchCode}/branding`,
    brandingData
  );
  return response.data;
};

const updateBrandColors = async (branchCode, colors) => {
  const response = await api.put(
    `${BASE_URL}/${branchCode}/brand-colors`,
    colors
  );
  return response.data;
};

const updateVisibility = async (branchCode, visibilitySettings) => {
  const response = await api.put(
    `${BASE_URL}/${branchCode}/visibility`,
    visibilitySettings
  );
  return response.data;
};

// ============================================
// LOGO & BANNER
// ============================================

const uploadLogo = async (branchCode, formData) => {
  const response = await api.post(`${BASE_URL}/${branchCode}/logo`, formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return response.data;
};

const deleteLogo = async (branchCode) => {
  const response = await api.delete(`${BASE_URL}/${branchCode}/logo`);
  return response.data;
};

const uploadBanner = async (branchCode, formData) => {
  const response = await api.post(
    `${BASE_URL}/${branchCode}/banner`,
    formData,
    {
      headers: { "Content-Type": "multipart/form-data" },
    }
  );
  return response.data;
};

// ============================================
// STORE IMAGES
// ============================================

const getStoreImages = async (branchCode) => {
  const response = await api.get(`${BASE_URL}/${branchCode}/images`);
  return response.data;
};

const uploadStoreImages = async (branchCode, formData) => {
  const response = await api.post(
    `${BASE_URL}/${branchCode}/images`,
    formData,
    {
      headers: { "Content-Type": "multipart/form-data" },
    }
  );
  return response.data;
};

const updateStoreImage = async (branchCode, imageIndex, imageData) => {
  const response = await api.put(
    `${BASE_URL}/${branchCode}/images/${imageIndex}`,
    imageData
  );
  return response.data;
};

const deleteStoreImage = async (branchCode, imageIndex) => {
  const response = await api.delete(
    `${BASE_URL}/${branchCode}/images/${imageIndex}`
  );
  return response.data;
};

const reorderStoreImages = async (branchCode, imageOrder) => {
  const response = await api.put(`${BASE_URL}/${branchCode}/images/reorder`, {
    imageOrder,
  });
  return response.data;
};

const setPrimaryImage = async (branchCode, imageIndex) => {
  const response = await api.patch(
    `${BASE_URL}/${branchCode}/images/${imageIndex}/set-primary`
  );
  return response.data;
};

// ============================================
// STORE VIDEOS
// ============================================

const getStoreVideos = async (branchCode) => {
  const response = await api.get(`${BASE_URL}/${branchCode}/videos`);
  return response.data;
};

const addStoreVideo = async (branchCode, videoData) => {
  const response = await api.post(
    `${BASE_URL}/${branchCode}/videos`,
    videoData
  );
  return response.data;
};

const updateStoreVideo = async (branchCode, videoIndex, videoData) => {
  const response = await api.put(
    `${BASE_URL}/${branchCode}/videos/${videoIndex}`,
    videoData
  );
  return response.data;
};

const deleteStoreVideo = async (branchCode, videoIndex) => {
  const response = await api.delete(
    `${BASE_URL}/${branchCode}/videos/${videoIndex}`
  );
  return response.data;
};

// ============================================
// HIGHLIGHTS/BADGES
// ============================================

const getHighlights = async (branchCode) => {
  const response = await api.get(`${BASE_URL}/${branchCode}/highlights`);
  return response.data;
};

const updateHighlights = async (branchCode, highlights) => {
  const response = await api.put(`${BASE_URL}/${branchCode}/highlights`, {
    highlights,
  });
  return response.data;
};

const addHighlight = async (branchCode, highlight) => {
  const response = await api.post(
    `${BASE_URL}/${branchCode}/highlights`,
    highlight
  );
  return response.data;
};

const deleteHighlight = async (branchCode, highlightIndex) => {
  const response = await api.delete(
    `${BASE_URL}/${branchCode}/highlights/${highlightIndex}`
  );
  return response.data;
};

// ============================================
// COLLECTIONS
// ============================================

const getCollections = async (branchCode) => {
  const response = await api.get(`${BASE_URL}/${branchCode}/collections`);
  return response.data;
};

const updateCollections = async (branchCode, collections) => {
  const response = await api.put(`${BASE_URL}/${branchCode}/collections`, {
    collections,
  });
  return response.data;
};

// ============================================
// SERVICES
// ============================================

const getServices = async (branchCode) => {
  const response = await api.get(`${BASE_URL}/${branchCode}/services`);
  return response.data;
};

const updateServices = async (branchCode, services) => {
  const response = await api.put(`${BASE_URL}/${branchCode}/services`, {
    services,
  });
  return response.data;
};

const addService = async (branchCode, service) => {
  const response = await api.post(
    `${BASE_URL}/${branchCode}/services`,
    service
  );
  return response.data;
};

const updateService = async (branchCode, serviceId, serviceData) => {
  const response = await api.put(
    `${BASE_URL}/${branchCode}/services/${serviceId}`,
    serviceData
  );
  return response.data;
};

const deleteService = async (branchCode, serviceId) => {
  const response = await api.delete(
    `${BASE_URL}/${branchCode}/services/${serviceId}`
  );
  return response.data;
};

// ============================================
// CERTIFICATIONS
// ============================================

const getCertifications = async (branchCode) => {
  const response = await api.get(`${BASE_URL}/${branchCode}/certifications`);
  return response.data;
};

const addCertification = async (branchCode, formData) => {
  const response = await api.post(
    `${BASE_URL}/${branchCode}/certifications`,
    formData,
    {
      headers: { "Content-Type": "multipart/form-data" },
    }
  );
  return response.data;
};

const updateCertification = async (branchCode, certificationId, data) => {
  const response = await api.put(
    `${BASE_URL}/${branchCode}/certifications/${certificationId}`,
    data
  );
  return response.data;
};

const deleteCertification = async (branchCode, certificationId) => {
  const response = await api.delete(
    `${BASE_URL}/${branchCode}/certifications/${certificationId}`
  );
  return response.data;
};

// ============================================
// AWARDS
// ============================================

const getAwards = async (branchCode) => {
  const response = await api.get(`${BASE_URL}/${branchCode}/awards`);
  return response.data;
};

const addAward = async (branchCode, formData) => {
  const response = await api.post(
    `${BASE_URL}/${branchCode}/awards`,
    formData,
    {
      headers: { "Content-Type": "multipart/form-data" },
    }
  );
  return response.data;
};

const updateAward = async (branchCode, awardId, data) => {
  const response = await api.put(
    `${BASE_URL}/${branchCode}/awards/${awardId}`,
    data
  );
  return response.data;
};

const deleteAward = async (branchCode, awardId) => {
  const response = await api.delete(
    `${BASE_URL}/${branchCode}/awards/${awardId}`
  );
  return response.data;
};

// ============================================
// TESTIMONIALS
// ============================================

const getTestimonials = async (branchCode) => {
  const response = await api.get(`${BASE_URL}/${branchCode}/testimonials`);
  return response.data;
};

const addTestimonial = async (branchCode, formData) => {
  const response = await api.post(
    `${BASE_URL}/${branchCode}/testimonials`,
    formData,
    {
      headers: { "Content-Type": "multipart/form-data" },
    }
  );
  return response.data;
};

const updateTestimonial = async (branchCode, testimonialIndex, data) => {
  const response = await api.put(
    `${BASE_URL}/${branchCode}/testimonials/${testimonialIndex}`,
    data
  );
  return response.data;
};

const deleteTestimonial = async (branchCode, testimonialIndex) => {
  const response = await api.delete(
    `${BASE_URL}/${branchCode}/testimonials/${testimonialIndex}`
  );
  return response.data;
};

// ============================================
// USPs
// ============================================

const updateUSPs = async (branchCode, usps) => {
  const response = await api.put(`${BASE_URL}/${branchCode}/usps`, { usps });
  return response.data;
};

// ============================================
// CONTACT INFO
// ============================================

const getContactInfo = async (branchCode) => {
  const response = await api.get(`${BASE_URL}/${branchCode}/contact-info`);
  return response.data;
};

const updateContactInfo = async (branchCode, contactInfo) => {
  const response = await api.put(
    `${BASE_URL}/${branchCode}/contact-info`,
    contactInfo
  );
  return response.data;
};

const updateSocialMedia = async (branchCode, socialMedia) => {
  const response = await api.put(
    `${BASE_URL}/${branchCode}/contact-info/social-media`,
    { socialMedia }
  );
  return response.data;
};

// ============================================
// TEAM MEMBERS
// ============================================

const getTeamMembers = async (branchCode) => {
  const response = await api.get(`${BASE_URL}/${branchCode}/team`);
  return response.data;
};

const addTeamMember = async (branchCode, formData) => {
  const response = await api.post(`${BASE_URL}/${branchCode}/team`, formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return response.data;
};

const updateTeamMember = async (branchCode, memberId, data) => {
  const response = await api.put(
    `${BASE_URL}/${branchCode}/team/${memberId}`,
    data
  );
  return response.data;
};

const deleteTeamMember = async (branchCode, memberId) => {
  const response = await api.delete(
    `${BASE_URL}/${branchCode}/team/${memberId}`
  );
  return response.data;
};

// ============================================
// STORE INFO & FACILITIES
// ============================================

const getStoreInfo = async (branchCode) => {
  const response = await api.get(`${BASE_URL}/${branchCode}/store-info`);
  return response.data;
};

const updateStoreInfo = async (branchCode, storeInfo) => {
  const response = await api.put(
    `${BASE_URL}/${branchCode}/store-info`,
    storeInfo
  );
  return response.data;
};

const updateFacilities = async (branchCode, facilities) => {
  const response = await api.put(
    `${BASE_URL}/${branchCode}/store-info/facilities`,
    { facilities }
  );
  return response.data;
};

const updatePaymentMethods = async (branchCode, paymentMethods) => {
  const response = await api.put(
    `${BASE_URL}/${branchCode}/store-info/payment-methods`,
    { paymentMethods }
  );
  return response.data;
};

const updateDirections = async (branchCode, directionsData) => {
  const response = await api.put(
    `${BASE_URL}/${branchCode}/store-info/directions`,
    directionsData
  );
  return response.data;
};

// Update full address (line1, line2, city, state, pinCode, landmark, lat/lon, googleMapsUrl, googlePlaceId)
const updateAddress = async (branchCode, addressData) => {
  const response = await api.put(
    `${BASE_URL}/${branchCode}/address`,
    addressData
  );
  return response.data;
};

// ============================================
// SEO
// ============================================

const getSEO = async (branchCode) => {
  const response = await api.get(`${BASE_URL}/${branchCode}/seo`);
  return response.data;
};

const updateSEO = async (branchCode, seoData) => {
  const response = await api.put(`${BASE_URL}/${branchCode}/seo`, seoData);
  return response.data;
};

// ============================================
// BULK OPERATIONS
// ============================================

const copyDisplayConfig = async (
  sourceBranchCode,
  targetBranchCodes,
  sections
) => {
  const response = await api.post(`${BASE_URL}/copy`, {
    sourceBranchCode,
    targetBranchCodes,
    sections,
  });
  return response.data;
};

const getDisplayOptions = async () => {
  const response = await api.get(`${BASE_URL}/options`);
  return response.data;
};

const branchDisplayService = {
  // Display & Branding
  getBranchDisplay,
  getPublicBranchDisplay,
  updateBranding,
  updateBrandColors,
  updateVisibility,

  // Logo & Banner
  uploadLogo,
  deleteLogo,
  uploadBanner,

  // Images
  getStoreImages,
  uploadStoreImages,
  updateStoreImage,
  deleteStoreImage,
  reorderStoreImages,
  setPrimaryImage,

  // Videos
  getStoreVideos,
  addStoreVideo,
  updateStoreVideo,
  deleteStoreVideo,

  // Highlights
  getHighlights,
  updateHighlights,
  addHighlight,
  deleteHighlight,

  // Collections
  getCollections,
  updateCollections,

  // Services
  getServices,
  updateServices,
  addService,
  updateService,
  deleteService,

  // Certifications
  getCertifications,
  addCertification,
  updateCertification,
  deleteCertification,

  // Awards
  getAwards,
  addAward,
  updateAward,
  deleteAward,

  // Testimonials
  getTestimonials,
  addTestimonial,
  updateTestimonial,
  deleteTestimonial,

  // USPs
  updateUSPs,

  // Contact Info
  getContactInfo,
  updateContactInfo,
  updateSocialMedia,

  // Team Members
  getTeamMembers,
  addTeamMember,
  updateTeamMember,
  deleteTeamMember,

  // Store Info
  getStoreInfo,
  updateStoreInfo,
  updateFacilities,
  updatePaymentMethods,
  updateDirections,
  updateAddress,

  // SEO
  getSEO,
  updateSEO,

  // Bulk Operations
  copyDisplayConfig,
  getDisplayOptions,
};

export default branchDisplayService;
